import React from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ownerApi } from "../../../../api/owner";
import { formatApiError } from "../../../../api/client";
import { dateInputValue, money } from "../center/ownerCenterUi";

function idOf(value) {
  return String(value?.id || value?._id || value || "");
}

function minutesLabel(value) {
  const minutes = Math.max(0, Number(value || 0));
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  if (!hours) return `${remainder} min`;
  return remainder ? `${hours} h ${remainder} min` : `${hours} h`;
}

function emptyForm() {
  return {
    title: "",
    kind: "follow_up",
    priority: "normal",
    scheduledStartAt: "",
    dueAt: "",
    estimatedMinutes: "",
    initiativeId: "",
    recurringPlanId: "",
    saleId: "",
    webProfitProjectId: "",
    notes: "",
  };
}

function formFromCommitment(commitment) {
  if (!commitment) return emptyForm();
  return {
    title: commitment.title || "",
    kind: commitment.kind || "task",
    priority: commitment.priority || "normal",
    scheduledStartAt: dateInputValue(commitment.scheduledStartAt),
    dueAt: dateInputValue(commitment.dueAt),
    estimatedMinutes: commitment.estimatedMinutes ? String(commitment.estimatedMinutes) : "",
    initiativeId: idOf(commitment.initiativeId),
    recurringPlanId: idOf(commitment.recurringPlanId),
    saleId: idOf(commitment.saleId),
    webProfitProjectId: idOf(commitment.webProfitProjectId),
    notes: commitment.notes || "",
  };
}

export default function OwnerClientCommitmentDialog({
  open,
  client,
  context,
  platformProjects = [],
  commitment = null,
  onClose,
  onSaved,
}) {
  const initiatives = context?.initiatives || [];
  const recurringPlans = (context?.recurringPlans || []).filter((row) => row.effectiveStatus !== "ended");
  const sales = (context?.sales || []).filter((row) => row.status !== "cancelled");
  const currentProjectId = idOf(commitment?.webProfitProjectId);
  const selectableProjects = platformProjects.filter((row) => row.ownerClientLink !== "detected" || idOf(row) === currentProjectId);
  const [form, setForm] = React.useState(() => formFromCommitment(commitment));
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    if (!open) return;
    setForm(formFromCommitment(commitment));
    setError("");
  }, [open, commitment]);

  const submit = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    setError("");
    const payload = {
      ...form,
      title: form.title.trim(),
      scheduledStartAt: form.scheduledStartAt || null,
      dueAt: form.dueAt || null,
      estimatedMinutes: form.estimatedMinutes ? Number(form.estimatedMinutes) : null,
      initiativeId: form.initiativeId || null,
      recurringPlanId: form.recurringPlanId || null,
      saleId: form.saleId || null,
      webProfitProjectId: form.webProfitProjectId || null,
    };
    try {
      if (commitment) {
        await ownerApi.updateCommitment(idOf(commitment), payload);
      } else {
        await ownerApi.createCommitment({
          ...payload,
          clientId: idOf(client),
          source: { kind: "manual" },
        });
      }
      await onSaved?.();
      onClose?.();
    } catch (err) {
      setError(formatApiError(err, commitment ? "No pude actualizar la actividad." : "No pude guardar la actividad."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pb: .7 }}>
        <Typography sx={{ fontWeight: 1000 }}>{commitment ? "Editar actividad" : `Nueva actividad · ${client?.name || "Cliente"}`}</Typography>
        <Typography sx={{ mt: .25, color: "text.secondary", fontSize: ".78rem" }}>
          Podés relacionarla con el frente Owner, mensualidad, venta y Project WebProfit sin duplicar ninguna de esas entidades.
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1.2} sx={{ pt: 1 }}>
          <TextField autoFocus fullWidth label="Qué tiene que pasar" value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.1}>
            <FormControl fullWidth>
              <InputLabel>Tipo</InputLabel>
              <Select label="Tipo" value={form.kind} onChange={(event) => setForm({ ...form, kind: event.target.value })}>
                <MenuItem value="task">Tarea</MenuItem>
                <MenuItem value="follow_up">Seguimiento</MenuItem>
                <MenuItem value="appointment">Reunión / cita futura</MenuItem>
                <MenuItem value="promise">Promesa</MenuItem>
                <MenuItem value="reminder">Recordatorio</MenuItem>
                <MenuItem value="project_next_step">Próximo paso de proyecto</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Prioridad</InputLabel>
              <Select label="Prioridad" value={form.priority} onChange={(event) => setForm({ ...form, priority: event.target.value })}>
                <MenuItem value="low">Baja</MenuItem>
                <MenuItem value="normal">Normal</MenuItem>
                <MenuItem value="high">Alta</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Tiempo estimado</InputLabel>
              <Select label="Tiempo estimado" value={form.estimatedMinutes} onChange={(event) => setForm({ ...form, estimatedMinutes: event.target.value })}>
                <MenuItem value="">Sin estimar</MenuItem>
                {[15, 30, 45, 60, 90, 120, 180, 240].map((value) => <MenuItem key={value} value={String(value)}>{minutesLabel(value)}</MenuItem>)}
              </Select>
            </FormControl>
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.1}>
            <TextField fullWidth type="datetime-local" label="Bloque en agenda" InputLabelProps={{ shrink: true }} value={form.scheduledStartAt} onChange={(event) => setForm({ ...form, scheduledStartAt: event.target.value })} />
            <TextField fullWidth type="datetime-local" label="Fecha límite" InputLabelProps={{ shrink: true }} value={form.dueAt} onChange={(event) => setForm({ ...form, dueAt: event.target.value })} />
          </Stack>
          <Divider><Typography sx={{ px: .8, color: "text.secondary", fontSize: ".68rem", fontWeight: 900 }}>CONTEXTO VINCULADO</Typography></Divider>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.1}>
            <FormControl fullWidth>
              <InputLabel>Frente Owner</InputLabel>
              <Select label="Frente Owner" value={form.initiativeId} onChange={(event) => setForm({ ...form, initiativeId: event.target.value })}>
                <MenuItem value="">Ninguno</MenuItem>
                {initiatives.map((row) => <MenuItem key={idOf(row)} value={idOf(row)}>{row.name}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Mensualidad Owner</InputLabel>
              <Select label="Mensualidad Owner" value={form.recurringPlanId} onChange={(event) => setForm({ ...form, recurringPlanId: event.target.value })}>
                <MenuItem value="">Ninguna</MenuItem>
                {recurringPlans.map((row) => <MenuItem key={idOf(row)} value={idOf(row)}>{row.name} · {money(row.projectedAmount, row.currency)}</MenuItem>)}
              </Select>
            </FormControl>
          </Stack>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.1}>
            <FormControl fullWidth>
              <InputLabel>Venta / propuesta</InputLabel>
              <Select label="Venta / propuesta" value={form.saleId} onChange={(event) => setForm({ ...form, saleId: event.target.value })}>
                <MenuItem value="">Ninguna</MenuItem>
                {sales.map((row) => <MenuItem key={idOf(row)} value={idOf(row)}>{row.title} · {money(row.outstanding ?? row.total, row.currency)}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Project WebProfit</InputLabel>
              <Select label="Project WebProfit" value={form.webProfitProjectId} onChange={(event) => setForm({ ...form, webProfitProjectId: event.target.value })}>
                <MenuItem value="">Ninguno</MenuItem>
                {selectableProjects.map((row) => <MenuItem key={idOf(row)} value={idOf(row)}>{row.name}{row.ownerClientLink === "detected" ? " · detectado por la cuenta" : ""}</MenuItem>)}
              </Select>
            </FormControl>
          </Stack>
          <TextField fullWidth multiline minRows={2} label="Contexto / notas de la actividad" value={form.notes} onChange={(event) => setForm({ ...form, notes: event.target.value })} />
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: "none", fontWeight: 850 }}>Cancelar</Button>
        <Button variant="contained" onClick={submit} disabled={saving || !form.title.trim()} sx={{ textTransform: "none", fontWeight: 950 }}>
          {saving ? "Guardando…" : commitment ? "Guardar cambios" : "Guardar actividad"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
